import firebase from "firebase/app";
import "firebase/functions";
import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import type { Cookie } from "src/utils/extension";

export type VintedCatalogTaxonomy = {
  department?: string;
  category?: string;
  subCategory?: string;
};

export type VintedCatalogIndex = Record<string, VintedCatalogTaxonomy>;

let vintedCatalogIndexPromise: Promise<VintedCatalogIndex> | null = null;

export async function vintedIntegrationCreate(args: {
  cookies: Cookie[];
  integrationId?: string;
  username: string;
  userId: string;
  lastSync?: string;
  bankAccountId?: string;
  userAgent?: string;
}) {
  const f = firebase.functions().httpsCallable("vinted-integrationCreate");
  const { userAgent = navigator.userAgent, ...rest } = args;
  return await f({ ...rest, userAgent });
}

export async function vintedIntegrationDelete(id: string) {
  const f = firebase.functions().httpsCallable("vinted-deleteIntegration");
  return await f({ integrationId: id });
}

export async function vintedIntegrationSyncChange(
  id: string,
  sync: boolean,
  inventory: boolean = true
) {
  const userId = await getUserId();
  const db = firestore();

  await db.runTransaction(async (transaction) => {
    const user = await transaction.get(db.collection("Users").doc(userId));
    await transaction.update(db.collection("Plaid_Integrations").doc(id), {
      sync: sync && user.data()?.plan === UserPlan.ULTIMATE,
      inventory: sync && inventory,
    });
  });
}

export async function vintedProcessFile(
  integrationId: string,
  type: "sales" | "inventory" | "transactions",
  importType: "initial-import" | "direct-import" | "daily-sync",
  filename: string,
  {
    uploadSalesId,
    uploadReturnsId,
    uploadInventoryId,
    uploadTransactionsId,
  }: {
    uploadSalesId?: string;
    uploadReturnsId?: string;
    uploadInventoryId?: string;
    uploadTransactionsId?: string;
  } = {}
) {
  const f = firebase.functions().httpsCallable("vinted-processFile");
  return await f({
    integrationId,
    importType,
    type,
    filename,
    uploadSalesId,
    uploadReturnsId,
    uploadInventoryId,
    uploadTransactionsId,
  });
}

export async function getVintedCatalogIndex(): Promise<VintedCatalogIndex> {
  if (!vintedCatalogIndexPromise) {
    vintedCatalogIndexPromise = firebase
      .functions()
      .httpsCallable("vinted-getCatalogIndex")()
      .then((res) => {
        const data = res.data as { catalogIndex?: VintedCatalogIndex };
        return data.catalogIndex || {};
      })
      .catch((error) => {
        vintedCatalogIndexPromise = null;
        throw error;
      });
  }
  return await vintedCatalogIndexPromise;
}

export async function getVintedIntegration() {
  const uid = await getUserId();
  const db = firestore();
  const integrations = await db
    .collection("Plaid_Integrations")
    .where("user", "==", uid)
    .where("type", "==", "vinted")
    .get();
  const integration = integrations.docs.pop();
  if (integration) {
    return integration.data();
  }
  return null;
}
